/**
 * 侧边栏菜单容器
 * 参考：pro-components/src/layout/components/SiderMenu/SiderMenu.tsx
 */

import { Layout } from "antd";
import type { CSSProperties, ReactNode } from "react";
import { Logo } from "../../assert/Logo";
import { BaseMenu } from "./BaseMenu";
import { Scrollbar } from "../../../Scrollbar";
import type { MenuDataItem, ProRouteObject, ProSettings } from "../../typing";
import { defaultSettings } from "../../defaultSettings";
import { CollapsedIcon } from "../CollapsedIcon";
import styles from "./SiderMenu.module.css";

const { Sider } = Layout;

export interface SiderMenuProps {
  /** Logo */
  logo?: ReactNode | string | false;
  /** 应用标题 */
  title?: string | false;
  /** 菜单数据 */
  menuData: MenuDataItem[];
  /** 路由配置 */
  routes?: ProRouteObject[];
  /** 是否收起 */
  collapsed?: boolean;
  /** 是否允许折叠 */
  collapsible?: boolean;
  /** 折叠回调 */
  onCollapse?: (collapsed: boolean) => void;
  /** 菜单头部自定义渲染 */
  menuHeaderRender?: (logo: ReactNode, title: ReactNode) => ReactNode;
  /** 菜单底部自定义渲染 */
  menuFooterRender?: (collapsed?: boolean) => ReactNode;
  /** 菜单项自定义渲染 */
  menuItemRender?: (item: MenuDataItem, defaultDom: ReactNode) => ReactNode;
  /** 布局设置 */
  settings?: ProSettings;
}

export const SiderMenu: React.FC<SiderMenuProps> = ({
  logo,
  title,
  menuData,
  routes,
  collapsed,
  collapsible,
  onCollapse,
  menuHeaderRender,
  menuFooterRender,
  menuItemRender,
  settings = {},
}) => {
  const finalSettings = { ...defaultSettings, ...settings };
  const currentCollapsed = Boolean(collapsed);
  const siderWidth = collapsed
    ? finalSettings.collapsedWidth
    : finalSettings.siderWidth;
  const wrapperStyle = {
    "--sider-menu-width":
      typeof siderWidth === "number" ? `${siderWidth}px` : siderWidth,
  } as CSSProperties;

  const handleCollapse = () => {
    if (!collapsible) return;
    onCollapse?.(!currentCollapsed);
  };

  // 渲染 Logo 区域
  const renderLogo = () => {
    const logoNode = <Logo logo={logo} title={title} collapsed={collapsed} />;

    if (menuHeaderRender) {
      return menuHeaderRender(logo || null, title || null);
    }

    return logoNode;
  };

  return (
    <div
      className={`${styles.container} ${
        finalSettings.fixSiderbar ? styles.fixed : styles.relative
      }`}
      style={wrapperStyle}
    >
      {/* Logo 区域 - 固定不滚动 */}
      <div className={styles.header}>
        {renderLogo()}
        {collapsible && (
          <div className={styles.collapseTrigger}>
            <CollapsedIcon
              collapsed={currentCollapsed}
              onClick={handleCollapse}
            />
          </div>
        )}
      </div>

      {/* Sider 菜单区域 - 可滚动 */}
      <Scrollbar className={styles.scrollArea} thumbWidth={6}>
        <Sider
          className={styles.sider}
          theme="light"
          width="100%"
          collapsedWidth="100%"
          collapsed={false}
          trigger={null}
        >
          <BaseMenu
            menuData={menuData}
            routes={routes}
            menuItemRender={menuItemRender}
            collapsed={collapsed}
          />
        </Sider>
      </Scrollbar>
      {/* 底部区域 */}
      {menuFooterRender && (
        <div className={styles.footer}>
          {menuFooterRender(collapsed)}
        </div>
      )}
    </div>
  );
};
